import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, LayoutGrid, List as ListIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOGO = "/bailey-logo.png";

const EMPTY = {
  first_name: "",
  last_name: "",
  birth_date: "",
  date_of_death: "",
  spouse_first_name: "",
  spouse_last_name: "",
  spouse_date_of_death: "",
  submitted_by: "",
  notes: "",
  photo_path: "",
};

function MemorialTile({ m, canDelete, onDelete, onOpen }) {
  const primaryName = `${m.first_name} ${m.last_name}`.trim();
  const yr = (s) => (s || "").slice(0, 4);
  const range = (b, d) => {
    if (b && d) return `${yr(b)} — ${yr(d)}`;
    if (d) return `d. ${yr(d)}`;
    return "";
  };
  return (
    <div
      className="relative group rounded-2xl overflow-hidden bg-white border border-[#e6e2d6] shadow-[0_10px_30px_-18px_rgba(26,41,71,0.25)]"
      data-testid={`memorial-tile-${m.id}`}
    >
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(m);
          }}
          className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 border border-stone-200 text-stone-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition flex items-center justify-center z-10"
          title="Remove"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={() => onOpen(m)}
        className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-[#7CB2E8] rounded-2xl overflow-hidden"
        data-testid={`memorial-tile-open-${m.id}`}
      >
        <div className="aspect-[3/4] bg-stone-100 overflow-hidden">
          <img
            src={`${API_BASE}/memorials/photo/${m.photo_path}`}
            alt={primaryName}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="p-4 text-center">
          <div className="font-serif text-lg text-stone-900">{primaryName}</div>
          <div className="text-xs text-stone-500 mt-0.5 tabular-nums">
            {range(m.birth_date, m.date_of_death)}
          </div>
          {m.family_surname && (
            <div
              className="text-[10px] tracking-[0.16em] uppercase text-[#8a7a3a] mt-2"
              data-testid={`memorial-tile-family-${m.id}`}
            >
              from the {m.family_surname} family
            </div>
          )}
        </div>
      </button>
    </div>
  );
}

function MemorialLightbox({ items, index, onClose, onNav }) {
  const touchRef = React.useRef({ x: 0, y: 0 });
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onNav(-1);
      else if (e.key === "ArrowRight") onNav(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      onNav(dx < 0 ? 1 : -1);
    }
  };

  const m = items[index];
  if (!m) return null;
  const yr = (s) => (s || "").slice(0, 4);
  const primaryName = `${m.first_name} ${m.last_name}`.trim();
  const spouseName = m.spouse_first_name
    ? `${m.spouse_first_name} ${m.spouse_last_name || m.last_name}`.trim()
    : "";
  const range = (b, d) => {
    if (b && d) return `${yr(b)} — ${yr(d)}`;
    if (d) return `d. ${yr(d)}`;
    if (b) return `b. ${yr(b)}`;
    return "";
  };
  const canPrev = items.length > 1;
  const canNext = items.length > 1;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="memorial-lightbox"
    >
      {canPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNav(-1);
          }}
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/95 hover:bg-white text-stone-700 items-center justify-center shadow-lg"
          aria-label="Previous memorial"
          data-testid="memorial-lightbox-prev"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {canNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNav(1);
          }}
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/95 hover:bg-white text-stone-700 items-center justify-center shadow-lg"
          aria-label="Next memorial"
          data-testid="memorial-lightbox-next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
      <div
        className="relative bg-[#fdfbf7] rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden grid md:grid-cols-[1.1fr_1fr]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/95 hover:bg-white text-stone-700 flex items-center justify-center shadow"
          aria-label="Close"
          data-testid="memorial-lightbox-close"
        >
          <X className="h-4 w-4" />
        </button>
        {m.photo_path && (
          <div className="bg-stone-100 max-h-[92vh] overflow-hidden">
            <img
              src={`${API_BASE}/memorials/photo/${m.photo_path}`}
              alt={primaryName}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6 md:p-8 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#7CB2E8]">
              In loving memory
            </div>
            {items.length > 1 && (
              <div
                className="text-[11px] tracking-[0.14em] uppercase text-stone-400 tabular-nums"
                data-testid="memorial-lightbox-counter"
              >
                {index + 1} / {items.length}
              </div>
            )}
          </div>
          <div className="font-serif text-3xl md:text-4xl text-stone-900 mt-2 leading-tight">
            {primaryName}
          </div>
          <div className="text-sm text-stone-500 mt-1 tabular-nums">
            {range(m.birth_date, m.date_of_death)}
          </div>
          {spouseName && (
            <div className="mt-6 pt-6 border-t border-[#eae5d9]">
              <div className="text-[11px] tracking-[0.14em] uppercase text-stone-400 mb-1">
                &amp; spouse
              </div>
              <div className="font-serif text-xl text-stone-800">{spouseName}</div>
              {m.spouse_date_of_death && (
                <div className="text-sm text-stone-500">
                  d. {yr(m.spouse_date_of_death)}
                </div>
              )}
            </div>
          )}
          {m.notes && (
            <p
              className="text-base text-stone-700 mt-6 italic leading-relaxed whitespace-pre-line"
              data-testid="memorial-lightbox-notes"
            >
              "{m.notes}"
            </p>
          )}
          {m.submitted_by && (
            <div className="text-xs text-stone-400 mt-6">
              — shared by {m.submitted_by}
              {m.family_surname && (
                <span className="ml-1">from the {m.family_surname} family</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MemorialCard({ m, canDelete, onDelete }) {
  const primaryName = `${m.first_name} ${m.last_name}`.trim();
  const spouseName = m.spouse_first_name
    ? `${m.spouse_first_name} ${m.spouse_last_name || m.last_name}`.trim()
    : "";
  const yr = (s) => (s || "").slice(0, 4);
  const range = (b, d) => {
    if (b && d) return `${yr(b)} — ${yr(d)}`;
    if (d) return `d. ${yr(d)}`;
    if (b) return `b. ${yr(b)}`;
    return "";
  };
  return (
    <div
      className="bg-white border border-[#e6e2d6] rounded-2xl p-6 relative group"
      data-testid={`memorial-card-${m.id}`}
    >
      {canDelete && (
        <button
          onClick={() => onDelete(m)}
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white border border-stone-200 text-stone-400 hover:text-red-600 hover:border-red-300 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
          title="Remove"
          data-testid={`memorial-delete-${m.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {m.photo_path && (
        <img
          src={`${API_BASE}/memorials/photo/${m.photo_path}`}
          alt={`${m.first_name} ${m.last_name}`}
          className="w-full h-56 object-cover rounded-xl mb-4 bg-stone-100"
          loading="lazy"
          data-testid={`memorial-photo-${m.id}`}
        />
      )}
      <div className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#7CB2E8]">
        In loving memory
      </div>
      <div className="font-serif text-2xl text-stone-900 mt-2">{primaryName}</div>
      <div className="text-sm text-stone-500 mt-0.5">
        {range(m.birth_date, m.date_of_death)}
      </div>
      {spouseName && (
        <div className="mt-4 pt-4 border-t border-[#eae5d9]">
          <div className="text-[11px] tracking-[0.14em] uppercase text-stone-400 mb-1">
            &amp; spouse
          </div>
          <div className="font-serif text-lg text-stone-800">{spouseName}</div>
          {m.spouse_date_of_death && (
            <div className="text-sm text-stone-500">
              d. {yr(m.spouse_date_of_death)}
            </div>
          )}
        </div>
      )}
      {m.notes && (
        <p className="text-sm text-stone-600 mt-4 italic leading-relaxed whitespace-pre-line">
          "{m.notes}"
        </p>
      )}
      {m.submitted_by && (
        <div className="text-xs text-stone-400 mt-4">
          — shared by {m.submitted_by}
          {m.family_surname && (
            <span className="ml-1">from the {m.family_surname} family</span>
          )}
        </div>
      )}
    </div>
  );
}

export default function InMemoriamPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const submittedByFromUrl = (searchParams.get("by") || "").trim();
  const familyToken = (searchParams.get("token") || "").trim();
  const canPost = !!familyToken;
  const [memorials, setMemorials] = useState([]);
  const [form, setForm] = useState({ ...EMPTY, submitted_by: submittedByFromUrl });
  const [showForm, setShowForm] = useState(canPost && searchParams.get("add") === "1");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" | "gallery"
  const [lightbox, setLightbox] = useState(null); // number | null (index into withPhoto)
  const [branch, setBranch] = useState("all");

  const branches = Array.from(
    new Set(memorials.map((m) => (m.family_surname || "").trim()).filter(Boolean))
  ).sort();
  const branchCounts = branches.reduce((acc, b) => {
    acc[b] = memorials.filter(
      (m) => (m.family_surname || "").trim() === b
    ).length;
    return acc;
  }, {});
  const filtered =
    branch === "all"
      ? memorials
      : memorials.filter((m) => (m.family_surname || "").trim() === branch);
  const withPhoto = filtered.filter((m) => m.photo_path);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!canPost) return toast.error("Open this from your family card to attach photos.");
    if (file.size > 10 * 1024 * 1024) {
      return toast.error("Photo too large. Max 10 MB.");
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("family_token", familyToken);
      const r = await api.post("/memorials/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_path: r.data.photo_path }));
      toast.success("Photo attached");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    try {
      const r = await api.get("/memorials");
      setMemorials(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load memorials");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canPost) return toast.error("Open this from your family card to post a memorial.");
    if (!form.first_name.trim() || !form.last_name.trim()) {
      return toast.error("Please add their first and last name.");
    }
    if (!form.date_of_death) {
      return toast.error("Please add the date of death.");
    }
    setBusy(true);
    try {
      await api.post("/memorials", { ...form, family_token: familyToken });
      toast.success("Memorial added — thank you.");
      setForm({ ...EMPTY, submitted_by: submittedByFromUrl });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add memorial.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove memorial for ${m.first_name} ${m.last_name}?`)) return;
    try {
      await api.delete(`/memorials/${m.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove");
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="max-w-6xl w-full mx-auto px-6 md:px-10 pt-8 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <img
            src={LOGO}
            alt="Bailey Family Reunion"
            className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-[#1a2947]/10"
          />
          <span className="font-serif text-lg sm:text-2xl text-stone-900 truncate">
            Bailey Family Reunion
          </span>
        </Link>
        <Link
          to="/"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#e6e2d6] bg-white px-3 py-2 text-xs sm:text-sm font-medium text-[#1a2947] hover:bg-[#1a2947] hover:text-white transition-colors"
          data-testid="memoriam-back-home"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back home</span>
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <div className="text-center mb-12">
          <div className="label-eyebrow text-[#7CB2E8] mb-4">In loving memory</div>
          <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
            Bailey family, remembered
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl mx-auto">
            A quiet page for the family members we've lost — we gather with them
            still in mind. To add someone, open your family card link and use the
            "Post to In Memoriam" button.
          </p>
          {canPost && !showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="mt-6 rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
              data-testid="memoriam-open-form-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add a memorial
            </Button>
          )}
        </div>

        {canPost && showForm && (
          <form
            onSubmit={submit}
            className="bg-white border border-[#e6e2d6] rounded-2xl p-6 md:p-8 mb-10 shadow-[0_20px_60px_-30px_rgba(26,41,71,0.20)]"
            data-testid="memoriam-form"
          >
            <div className="label-eyebrow mb-4">Bailey family member</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label className="label-eyebrow">First name</Label>
                <Input
                  value={form.first_name}
                  onChange={set("first_name")}
                  required
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-first-name"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Last name</Label>
                <Input
                  value={form.last_name}
                  onChange={set("last_name")}
                  required
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-last-name"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Birth date (optional)</Label>
                <Input
                  type="date"
                  value={form.birth_date}
                  onChange={set("birth_date")}
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-birth-date"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Date of death</Label>
                <Input
                  type="date"
                  value={form.date_of_death}
                  onChange={set("date_of_death")}
                  required
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-date-of-death"
                />
              </div>
            </div>

            <div className="label-eyebrow mb-4 pt-4 border-t border-[#eae5d9]">
              Spouse (if also passed)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label className="label-eyebrow">Spouse first name</Label>
                <Input
                  value={form.spouse_first_name}
                  onChange={set("spouse_first_name")}
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-spouse-first"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Spouse last name</Label>
                <Input
                  value={form.spouse_last_name}
                  onChange={set("spouse_last_name")}
                  placeholder={form.last_name || ""}
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-spouse-last"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Spouse date of death</Label>
                <Input
                  type="date"
                  value={form.spouse_date_of_death}
                  onChange={set("spouse_date_of_death")}
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-spouse-dod"
                />
              </div>
            </div>

            <div className="label-eyebrow mb-4 pt-4 border-t border-[#eae5d9]">
              Photo (optional)
            </div>
            <div className="mb-6 flex items-start gap-4 flex-wrap">
              {form.photo_path ? (
                <div className="relative">
                  <img
                    src={`${API_BASE}/memorials/photo/${form.photo_path}`}
                    alt="preview"
                    className="h-24 w-24 object-cover rounded-xl border border-[#e6e2d6]"
                    data-testid="memoriam-photo-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, photo_path: "" }))}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-stone-300 text-stone-500 hover:text-red-600 flex items-center justify-center"
                    title="Remove"
                    data-testid="memoriam-photo-remove"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <label
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full border-2 border-dashed border-stone-300 text-stone-600 hover:bg-stone-50 cursor-pointer text-sm"
                  data-testid="memoriam-photo-upload-label"
                >
                  {uploading ? "Uploading…" : "+ Attach a photo"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                    disabled={uploading}
                    data-testid="memoriam-photo-input"
                  />
                </label>
              )}
              <p className="text-xs text-stone-500 max-w-sm mt-2">
                JPG, PNG, or HEIC — up to 10 MB. Portrait works best.
              </p>
            </div>

            <div className="label-eyebrow mb-4 pt-4 border-t border-[#eae5d9]">
              A few words (optional)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
              <div>
                <Label className="label-eyebrow">A memory or note</Label>
                <Textarea
                  value={form.notes}
                  onChange={set("notes")}
                  rows={3}
                  placeholder="Loved her Sunday cornbread. Told the best stories on the porch."
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-notes"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Shared by (optional)</Label>
                <Input
                  value={form.submitted_by}
                  onChange={set("submitted_by")}
                  placeholder="Your name or relation"
                  className="mt-1 bg-stone-50 border-stone-200"
                  data-testid="memoriam-submitted-by"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowForm(false);
                  setForm(EMPTY);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
                data-testid="memoriam-submit"
              >
                {busy ? "Saving…" : "Add memorial"}
              </Button>
            </div>
          </form>
        )}

        {memorials.length === 0 ? (
          <div className="text-center text-stone-500 py-10">
            No memorials yet — be the first to remember someone.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
              {branches.length > 1 ? (
                <select
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 focus:outline-none focus:ring-2 focus:ring-[#7CB2E8]"
                  data-testid="memoriam-branch-filter"
                >
                  <option value="all">All Bailey branches · {memorials.length}</option>
                  {branches.map((b) => (
                    <option key={b} value={b}>
                      The {b} family · {branchCounts[b]}
                    </option>
                  ))}
                </select>
              ) : (
                <span />
              )}
              {withPhoto.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    className={
                      "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition " +
                      (viewMode === "list"
                        ? "bg-[#1a2947] text-white"
                        : "bg-white border border-stone-200 text-stone-500 hover:text-stone-700")
                    }
                    data-testid="memoriam-view-list"
                  >
                    <ListIcon className="h-3.5 w-3.5" /> All
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("gallery")}
                    className={
                      "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition " +
                      (viewMode === "gallery"
                        ? "bg-[#1a2947] text-white"
                        : "bg-white border border-stone-200 text-stone-500 hover:text-stone-700")
                    }
                    data-testid="memoriam-view-gallery"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" /> Gallery ({withPhoto.length})
                  </button>
                </div>
              )}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center text-stone-500 py-10">
                No memorials from the {branch} family yet.
              </div>
            ) : viewMode === "gallery" ? (
              <div
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5"
                data-testid="memoriam-gallery"
              >
                {withPhoto.map((m, idx) => (
                  <MemorialTile
                    key={m.id}
                    m={m}
                    canDelete={!!user}
                    onDelete={remove}
                    onOpen={() => setLightbox(idx)}
                  />
                ))}
              </div>
            ) : (
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-5"
                data-testid="memoriam-grid"
              >
                {filtered.map((m) => (
                  <MemorialCard
                    key={m.id}
                    m={m}
                    canDelete={!!user}
                    onDelete={remove}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>
      {lightbox !== null && withPhoto[lightbox] && (
        <MemorialLightbox
          items={withPhoto}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNav={(delta) =>
            setLightbox((i) =>
              i === null ? i : (i + delta + withPhoto.length) % withPhoto.length
            )
          }
        />
      )}
    </div>
  );
}
